import { ExtendedFormObject, ExtendedFormState } from '../ExtendedForm';
import { useEffect, useState, useMemo } from 'react';

export interface MultiExtendedFormHookProps<T extends object> {
    ids: string[];
    onFormFinish?: (id: string, values: T) => void;
}

export type ExtendedFormObjects<T extends object> = Record<string, ExtendedFormObject<T>>;

export const useMultiExtendedForm = <T extends object>(
    props: MultiExtendedFormHookProps<T>,
): [Record<string, ExtendedFormState>, ExtendedFormObjects<T>] => {
    const formIds = props.ids.join(',');
    const [reactiveStates, setReactiveStates] = useState<Record<string, ExtendedFormState>>({});

    const extendedFormObjects = useMemo<ExtendedFormObjects<T>>(
        () =>
            props.ids.reduce((acc, id) => {
                acc[id] = {
                    updateState: (state) => {
                        setReactiveStates((prevStates) => {
                            const prevState = prevStates[id];
                            return {
                                ...prevStates,
                                [id]: {
                                    ...prevState,
                                    ...state,
                                },
                            };
                        });
                    },
                    onFinish: (values) => {
                        props.onFormFinish?.(id, values);
                    },
                };
                return acc;
            }, {}),
        [formIds],
    );

    useEffect(() => {
        setReactiveStates((prevStates) => {
            const defaultState: ExtendedFormState = { isDirty: false, isInvalid: false };
            const newStates = { ...prevStates };

            // Add states for new IDs
            props.ids.forEach((id) => {
                if (!(id in newStates)) {
                    newStates[id] = defaultState;
                }
            });

            // Remove states for old IDs
            const currentIds = new Set(props.ids);
            Object.keys(newStates).forEach((id) => {
                if (!currentIds.has(id)) {
                    delete newStates[id];
                }
            });

            return newStates;
        });
    }, [formIds]);

    return useMemo(() => [reactiveStates, extendedFormObjects] as const, [reactiveStates, extendedFormObjects]);
};
